import PropTypes from 'prop-types';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { ValidationErrorContext } from '@bingads-webui-cc-react/validation-error-context';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { Select } from '@bingads-webui-react/primitive-with-signal';
import { addressPropType, i18nPropType, PropTypeBaseAddPIForm, PropTypeExistingPis } from '../shapes';
import { ExistingPaymentInstrumentSelector } from './existing-payment-instrument-selector';
import { RenderAddPIForm } from './render-add-pi-form';
import { PiTypeNames, IsPiError } from '../constants';

const currencyCaseFix = currency => currency.toUpperCase();

const isPiTypeValid = (piType, paymentType, currency, country, supportedPiTypes) => {
  const {
    supportedPaymentType,
    supportedCurrencies,
    useSupportedCountryList,
    supportedCountries,
  } = piType;

  return _.contains(supportedPiTypes, piType.id)
    && (_.isNull(supportedPaymentType) || supportedPaymentType === paymentType)
    && _.contains(supportedCurrencies, currency)
    && (!useSupportedCountryList || _.contains(supportedCountries, country));
};

const addPiTypeNames = (piTypeList, i18n) => _.map(
  piTypeList,
  piType => ({
    ...piType,
    supportedCurrencies: _.map(piType.supportedCurrencies, currencyCaseFix),
    value: typeof PiTypeNames[piType.id] === 'string' ? i18n.getString(PiTypeNames[piType.id]) : undefined,
  })
);

const getNewPaymentInstrumentList =
  (piTypeList, paymentType, currency, country, supportedPiTypes) => _.filter(
    piTypeList,
    piType => isPiTypeValid(piType, paymentType, currency, country, supportedPiTypes)
  );

export const PaymentInstrumentSelector = ({
  address,
  cid,
  country,
  offlinePiCountryList,
  currency,
  i18n,
  paymentAdderOptions,
  paymentType,
  setIsValid,
  setOfflinePiAddress,
  existingPis,
  piid,
  piType,
  setPiid,
  setPiType,
  setCountry,
  showPiTypeOptions,
  pidlAdderClassName,
  shouldRetryWithPopup,
  shouldAuthenticateWithPopup,
  permissions,
}) => {
  const clearPiType = useCallback(() => setPiType(Constants.PaymentInstrumentType.Unset), [setPiType]);
  const [pidlIsValid, setPidlIsValid] = useState(false);
  const [offlineIsValid, setOfflineIsValid] = useState(false);
  const { validationErrors, setValidationErrors } = useContext(ValidationErrorContext);
  const {
    paymentContext: {
      PaymentInstrumentConfigs,
      SupportedPiTypes,
    },
  } = useContext(PaymentContext);
  const { dynamic: { PreAuthPostpaidPhase1: isPreAuthPostpaidPhase1Enabled } = {} } = permissions;

  const paymentMethodErrors = useMemo(
    () => _.chain(validationErrors)
      .filter(IsPiError)
      .map(({ Message, DangerousHtmlMessage }) => (DangerousHtmlMessage
        ? { DangerousHtmlMessage }
        : Message))
      .value(),
    [validationErrors]
  );

  // Address is null on some stored payment instruments. Signup never saw it because a
  // new customer has none; editing an existing account does.
  const piInfoMap = useMemo(
    () => _.reduce(
      existingPis,
      (
        dict,
        {
          Id,
          Address,
          Type,
        }
      ) => ({
        ...dict,
        [Id]: {
          Country: Address ? Address.Country : undefined,
          Type,
        },
      }),
      {}
    ),
    [existingPis]
  );

  // if piid selected, unsetPiType
  useLayoutEffect(
    () => {
      if (_.isNumber(piid)) {
        setCountry(piInfoMap[piid].Country);
        setPiType(piInfoMap[piid].Type);
      }
    },
    [piid, setCountry, piInfoMap, setPiType]
  );

  useLayoutEffect(
    () => {
      const isOfflinePI = piType === Constants.PaymentInstrumentType.OfflinePaymentMethod;
      setIsValid(piid !== null
        || (
          piType !== Constants.PaymentInstrumentType.Unset
          && (
            (isOfflinePI && offlineIsValid)
            || (!isOfflinePI && pidlIsValid)
          )
        ));
    },
    [pidlIsValid, offlineIsValid, piType, piid, setIsValid]
  );

  useLayoutEffect(
    () => {
      const isOfflinePI = piType === Constants.PaymentInstrumentType.OfflinePaymentMethod;
      const isPidlPI = piType !== Constants.PaymentInstrumentType.Unset && !isOfflinePI;

      if (!isOfflinePI) {
        setOfflineIsValid(false);
        setOfflinePiAddress(null);
      }
      if (!isPidlPI) {
        setPidlIsValid(false);
      }
    },
    [piType, setOfflinePiAddress]
  );

  const piTypesWithNames = useMemo(
    () => addPiTypeNames(PaymentInstrumentConfigs, i18n),
    [PaymentInstrumentConfigs, i18n]
  );

  const filteredPiList = useMemo(
    () => getNewPaymentInstrumentList(
      piTypesWithNames,
      paymentType,
      currencyCaseFix(currency),
      country,
      SupportedPiTypes
    ),
    [country, currency, paymentType, piTypesWithNames, SupportedPiTypes]
  );

  const allowedPiTypes = useMemo(() => _.pluck(filteredPiList, 'id'), [filteredPiList]);

  const allowedPiList = useMemo(
    () => _.union(
      [{
        id: Constants.PaymentInstrumentType.Unset,
        value: i18n.getString(_TL_('Select one')),
      }],
      filteredPiList
    ),
    [filteredPiList, i18n]
  );

  // if piType not in updated list, unselect piType
  useLayoutEffect(
    () => {
      if (_.where(allowedPiList, { id: piType }).length === 0) {
        clearPiType();
      }
    },
    [
      clearPiType,
      allowedPiList,
      piType,
    ]
  );

  const selectOptions = useMemo(
    () => allowedPiList.map(({ id, value }) => <option key={id} value={id}>{value}</option>),
    [allowedPiList]
  );


  const shouldShowPrepayPreAuthInfoAlert = useMemo(() => piType === Constants.PaymentInstrumentType.CreditCard && paymentType === Constants.PaymentTypeId.Prepay, [paymentType, piType]);
  const shouldShowPostpaidPreAuthInfoAlert = useMemo(() => {
    const isIndiaABL = address.Country === Constants.CountryCode.IN;
    const isPostpaidCreditCard = paymentType === Constants.PaymentTypeId.Postpay && piType === Constants.PaymentInstrumentType.CreditCard;
    return isPreAuthPostpaidPhase1Enabled && isPostpaidCreditCard && !isIndiaABL;
  }, [address.Country, isPreAuthPostpaidPhase1Enabled, paymentType, piType]);

  const clearValidationErrorsIfNecessary = useCallback(
    () => {
      if (!_.isEmpty(paymentMethodErrors)) {
        setValidationErrors(_.reject(validationErrors, IsPiError));
      }
    },
    [paymentMethodErrors, validationErrors, setValidationErrors]
  );

  const onPiTypeSelect = useCallback(
    ({
      target: { value: newValue },
    }) => {
      clearValidationErrorsIfNecessary();
      setPiType(Number(newValue));
    },
    [clearValidationErrorsIfNecessary, setPiType]
  );

  const updatePiid = useCallback(
    (...args) => {
      clearValidationErrorsIfNecessary();
      setPiid(...args);
    },
    [clearValidationErrorsIfNecessary, setPiid]
  );

  const onOfflinePiFormValidate = useCallback(
    (addr, isValid) => {
      clearValidationErrorsIfNecessary();
      setOfflineIsValid(isValid);
      setOfflinePiAddress(addr);
    },
    [clearValidationErrorsIfNecessary, setOfflineIsValid, setOfflinePiAddress]
  );

  return (
    <div>
      <div className="piid-selector-container">
        <ExistingPaymentInstrumentSelector
          i18n={i18n}
          supportedPiTypes={allowedPiTypes}
          existingPis={existingPis}
          piid={piid}
          setPiid={updatePiid}
          setLoading={paymentAdderOptions.setLoading}
          paymentMethodErrors={paymentMethodErrors}
          setPiType={setPiType}
        />
      </div>
      {piid === null && (
        <div className="pi-type-selector-container">
          {showPiTypeOptions &&
            <LineByLineFormGroup
              label={i18n.getString(_TL_('Choose a payment method'))}
              helpPopupTopic="pop_BA_SmartCampaigns_Setup_PaymentMethod"
              helpPopupAriaLabel={i18n.getString(_TL_('Payment methods tooltip'))}
              static
            >
              <Select
                value={piType}
                onChange={onPiTypeSelect}
                className="pi-type-selector"
                name="pi type selection"
                aria-label={i18n.getString(_TL_('Payment methods dropdown'))}
              >
                {selectOptions}
              </Select>
              {shouldShowPrepayPreAuthInfoAlert &&
                <InlineInfoAlert>
                  {i18n.getString(_TL_('We’ll verify your card by making a charge, which will be removed once the card has been successfully authorized.'))}
                </InlineInfoAlert>}
              {shouldShowPostpaidPreAuthInfoAlert &&
                <InlineInfoAlert>
                  {i18n.getString(_TL_('We’ll verify your card by making a charge, which will be removed once the card has been successfully authorized. Then, we’ll make an authorization hold charge for the estimated transaction amount to verify your card for the postpaid advertising fee. This authorization hold will be removed either when the time limit for the hold is up or upon receipt of funds for the final transaction, whichever comes first.'))}
                </InlineInfoAlert>}
            </LineByLineFormGroup>
          }
          <ErrorMessages messages={paymentMethodErrors} />
          <RenderAddPIForm
            pidlAdderClassName={pidlAdderClassName}
            cid={cid}
            currency={currency}
            paymentAdderOptions={paymentAdderOptions}
            i18n={i18n}
            address={address}
            piType={piType}
            clearPiType={clearPiType}
            country={country}
            offlinePiCountryList={offlinePiCountryList}
            setPidlIsValid={setPidlIsValid}
            onOfflinePiFormValidate={onOfflinePiFormValidate}
            shouldRetryWithPopup={shouldRetryWithPopup}
            shouldAuthenticateWithPopup={shouldAuthenticateWithPopup}
            permissions={permissions}
          />
        </div>
      )}
    </div>
  );
};

PaymentInstrumentSelector.displayName = 'PaymentInstrumentSelector';
PaymentInstrumentSelector.propTypes = {
  cid: PropTypes.number.isRequired,
  paymentAdderOptions: PropTypeBaseAddPIForm.isRequired,
  i18n: i18nPropType.isRequired,
  address: addressPropType.isRequired,
  offlinePiCountryList: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Code: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  })).isRequired,
  country: PropTypes.string.isRequired,
  paymentType: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  setIsValid: PropTypes.func.isRequired,
  setOfflinePiAddress: PropTypes.func.isRequired,
  existingPis: PropTypeExistingPis.isRequired,
  piid: PropTypes.number,
  piType: PropTypes.number.isRequired,
  setPiid: PropTypes.func.isRequired,
  setPiType: PropTypes.func.isRequired,
  setCountry: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}),
  showPiTypeOptions: PropTypes.bool,
  pidlAdderClassName: PropTypes.string,
  shouldRetryWithPopup: PropTypes.bool,
  shouldAuthenticateWithPopup: PropTypes.bool,
};
PaymentInstrumentSelector.defaultProps = {
  piid: null,
  showPiTypeOptions: true,
  pidlAdderClassName: '',
  shouldRetryWithPopup: false,
  shouldAuthenticateWithPopup: false,
  permissions: {},
};
